import { InputText } from 'primereact/inputtext';
import React from 'react';
import styled from 'styled-components';

const StyledDomainInputRow = styled.div`
  display: flex;
  align-items: center;
  gap: var(--xsmall-padding);
  padding: var(--default-padding-small);
  border-radius: var(--xsmall-border-radius);
  border: 1px solid var(--gray-dark);

  .p-inputtext {
    flex: 1;
    min-width: 0;
    padding: 0;
    height: auto;
    border-radius: 0;
    border: none;
    box-shadow: none;
    background: transparent;

    &:focus,
    &:active {
      box-shadow: none;
      border: none;
    }

    &::placeholder {
      color: var(--gray-darker);
    }
  }
`;

const StyledInlineMessage = styled.span<{ variant: 'success' | 'error' }>`
  flex: 1;
  font-size: var(--xsmall-font-size);
  font-weight: 500;
  line-height: 1.5;
  padding-top: var(--xxxsmall-padding);
  color: ${({ variant }) =>
    variant === 'success' ? 'var(--green-main)' : 'var(--red-main)'};
`;

const StyledAddButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: var(--medium-big-padding);
  height: var(--medium-big-padding);
  border-radius: var(--xsmall-border-radius);
  background-color: var(--red-dark);
  border: none;
  cursor: pointer;
  flex-shrink: 0;

  i {
    color: var(--white-main);
    font-size: var(--small-font-size);
    transform: rotate(45deg);
  }

  &:hover {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

type DomainInputMessage = {
  text: string;
  variant: 'success' | 'error';
};

type DomainInputProps = {
  value: string;
  onChange: (value: string) => void;
  onAdd: () => void;
  placeholder?: string;
  message?: DomainInputMessage | null;
  disabled?: boolean;
};

export const DomainInput: React.FC<DomainInputProps> = ({
  value,
  onChange,
  onAdd,
  placeholder,
  message,
  disabled,
}) => {
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !disabled && value.trim()) {
      e.preventDefault();
      onAdd();
    }
  };

  return (
    <StyledDomainInputRow>
      <InputText
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        style={
          message
            ? { flex: 'none', width: `${Math.max(value.length, 1)}ch` }
            : undefined
        }
      />
      {message && (
        <StyledInlineMessage variant={message.variant}>
          {message.text}
        </StyledInlineMessage>
      )}
      <StyledAddButton
        onClick={onAdd}
        disabled={disabled || !value.trim()}
        type="button"
        aria-label="Add domain"
      >
        <i className="pi pi-times" aria-hidden="true" />
      </StyledAddButton>
    </StyledDomainInputRow>
  );
};
